import { defineStore, acceptHMRUpdate } from 'pinia'
import siteConfig from '@/config.json'
import axios from 'axios'
import sha1 from 'js-sha1'

import { normalizeEmail } from '@/helpers/general'
import { getStoredSession, clearStoredSession, isSessionExpired } from '@/helpers/session'
import { usDateFormatToYYYYMMDD, dateToYYYYMMDD, getDateStandard } from '@/helpers/datetime'
import { shouldCollectClassForInstructor, shouldIncludeInstructorClass } from '@/helpers/instructorClasses'
import { useClassStore } from '@/stores/ClassStore'
import { useCartStore } from '@/stores/CartStore'
import router, { goHome } from '@/router'
import { useRoute } from 'vue-router'

export const useUserStore = defineStore('UserStore', {
  state: () => {
    return {
      loggedIn: false,
      loggedOut: false,
      redirectTo: null,
      firstName: null,
      account: null,
      token: null,
      loading: true,
      bendLoggedIn: false,
      preAuthorizedEmails: [],
      accountItems: [],
      balance: 0,
      credit: 0,
      fetchedBalance: false,
      fetchingBalance: false,
      campaigns: [],
      fetchedMemberships: false,
      membershipsInHousehold: [],
      groupIds: [],
      registrationFees: [],
      savedPayments: [],
    }
  },

  getters: {
    isStaff() {
      return this.account && (!!this.account.staff || this.account?.email === 'kostas@artcentercanvas.com')
    },

    isActiveInstructor() {
      return this.account?.instructor
    },

    isJuror() {
      return this.account?.artist
    },

    wholeHouseholdMembership() {
      const mem = this.membershipsInHousehold?.find((m) => {
        if (m.household === 1 && m.expire_date) {
          // console.warn("hmmmm")
          const expDate = m.expire_date.slice(0, 10)
          const currentDate = dateToYYYYMMDD(new Date())
          if (currentDate <= expDate) {
            return true
          }
        }
        return false
      })
      if (!mem) return null
      return mem
    },

    hasWholeHouseholdMembership() {
      return !!this.wholeHouseholdMembership
    },

    hasWholeHouseholdMembershipWithDiscount() {
      const mem = this.wholeHouseholdMembership
      return !!mem && mem.discounts
    },
    memberWithActiveMembership() {
      const today = new Date().toISOString()
      const membership = this.membershipsInHousehold?.find((m) => m.expire_date > today)

      return !!membership
    },
    isActiveMember() {
      if (this.hasWholeHouseholdMembership) {
        return true
      }

      // XXX Temp fix
      // if (siteConfig.key === 'potters' && this.account?.member) return true

      if (!this.account || !this.account.member) return false

      let todaysDateStandard = getDateStandard()

      const expDate = usDateFormatToYYYYMMDD(this.account.memberExpirationDate, 2000, true)
      return expDate > todaysDateStandard
    },

    isPreAuthorized() {
      return this.loggedIn && this.account && this.preAuthorizedEmails.includes(sha1(this.account.email))
    },

    defaultDonationCampaign() {
      if (!this.campaigns || !siteConfig.features?.campaigns?.enabled) {
        return null
      }
      const route = useRoute()
      const defaultId = route?.query?.campaignId || siteConfig.features?.campaigns?.defaultId

      return (
        this.campaigns?.find((c) => c.campaignId === defaultId) || // removing this || this.campaigns?.filter(c => c.publish === true && c.closed === false)?.[0]
        this.campaigns?.[0]
      )
    },

    hasCompleteProfile() {
      let ok = true

      if (!this.account) {
        return ok
      }

      ok = ok && !!this.account.firstName
      ok = ok && !!this.account.lastName
      ok = ok && !!this.account.email

      if (ok && !siteConfig.features.genderIsOptionalInProfiles) {
        ok = ok && !!this.account.gender
      }

      if (
        ok &&
        !siteConfig.features.hideDateOfBirthFieldsOnRegistration &&
        !siteConfig.features.dateOfBirthIsOptionalInProfiles
      ) {
        ok = ok && !!this.account.dob
      }

      ok = ok && !!this.account.homePhone

      if (siteConfig.features.onlyRequireZipCodeForAddress) {
        ok = ok && !!this.account.addrZip
      } else {
        ok = ok && !!this.account.addrStreet1
        ok = ok && !!this.account.addrCity
        ok = ok && !!this.account.addrState
        ok = ok && !!this.account.addrZip
      }

      if (ok && siteConfig.features.includeRaceFieldInProfiles && !siteConfig.features.raceIsOptionalInProfiles) {
        ok = ok && !!this.account.race
      }
      if (ok && siteConfig.features.includeEthnicityFieldInProfiles) {
        ok = ok && !!this.account.ethnicity
      }
      if (ok && siteConfig.features.udef1Required) {
        ok = ok && !!this.account.udef1
      }
      if (ok && siteConfig.features.udef2Required) {
        ok = ok && !!this.account.udef2
      }
      if (
        ok &&
        !siteConfig.features.hideEmergencyContactInfoOnRegistration &&
        siteConfig.features.requireEmergencyContactInfo
      ) {
        ok = ok && !!this.account.emergencyContact
      }

      return ok
    },
  },

  actions: {
    // Must be an action, not a getter: Pinia caches getter Promises and Vue
    // stops tracking deps after the first await, so My Classes was racing
    // between class-level SQL results and session-instructor rows in openClasses.
    async listInstructorClassIds() {
      if (!this.isActiveInstructor) return []

      const classStore = useClassStore()
      const instructorId = this.account?.personId
      if (instructorId == null) return []

      const today = getDateStandard()
      const includePast = siteConfig.features?.instructorMyClassesIncludePast !== false
      const options = { today, includePast }

      // Session-instructor rows only exist on the open catalog. Wait for it so
      // this list is not a coin-flip against App.vue's fetchOpenClasses().
      await classStore.fetchOpenClasses()

      const startDates = new Map()
      try {
        const { data } = await axios.get(
          `${siteConfig.apiBaseUrl}/reports/queries/class-by-instructor-id?instructor_id=${instructorId}&instructor2_id=${instructorId}`
        )
        if (data.ok) {
          for (const cls of data.results || []) {
            const endDate = `${cls.end_date || ''}`.slice(0, 10)
            if (!includePast && endDate && endDate < today) continue
            startDates.set(`${cls.catalog_id}`, `${cls.start_date || ''}`.slice(0, 10))
          }
        }
      } catch (error) {
        console.error('Error fetching classes by instructor id', error)
      }

      classStore.openClasses.forEach((cls) => {
        if (shouldCollectClassForInstructor(cls, instructorId, options)) {
          startDates.set(`${cls.catalogId}`, cls.startDateStandard || startDates.get(`${cls.catalogId}`) || '')
        }
      })

      const ascending = siteConfig.features?.instructorClassSortOrder === 'ascending'
      return [...startDates.entries()]
        .sort(([, a], [, b]) => (ascending ? a.localeCompare(b) : b.localeCompare(a)))
        .map(([id]) => id)
    },

    async fetchInstructorClassBatch(catalogIds) {
      const instructorId = this.account?.personId
      if (instructorId == null || !catalogIds?.length) return []

      const classStore = useClassStore()
      const includePast = siteConfig.features?.instructorMyClassesIncludePast !== false
      const options = { today: getDateStandard(), includePast }

      const classes = await Promise.all(catalogIds.map((id) => classStore.fetchClassById(id)))
      return classes.filter((cls) => shouldIncludeInstructorClass(cls, instructorId, options))
    },

    async fetchInstructorClasses() {
      const ids = await this.listInstructorClassIds()
      return this.fetchInstructorClassBatch(ids)
    },

    isActiveMemberWithDiscount(userId = this.account?.personId) {
      if (this.hasWholeHouseholdMembershipWithDiscount) return true

      const membership = this.membershipsInHousehold?.find((m) => m.member_id === userId)

      return !!(membership && membership.discounts === 1 && membership?.expire_date > new Date().toISOString())
    },

    userProfile(id) {
      return this.account?.householdMembers?.find((person) => person.personId === id)
    },

    discountPercentForUser(userId = this.account?.personId, type = 'class') {
      if (!siteConfig.features.dynamicMemberPrice) return
      const membership =
        this.wholeHouseholdMembership || this.membershipsInHousehold?.find((m) => m.member_id === userId)
      const membershipsInCart = useCartStore()?.membershipsInCart
      if (!membership && !membershipsInCart) return

      membership ? (membership.categoryId = membership?.category_id) : null // Standardize categoryId property name
      const highestDiscount = [...membershipsInCart, membership]
        ?.filter((m) => m !== null && m !== undefined)
        ?.reduce((acc, m) => {
          const discount = siteConfig.memberships.find((mem) => mem.categoryId === m.categoryId)?.discounts?.[type]

          // Check if discount is defined and has an 'amount' property
          if (discount && typeof discount.amount === 'number') {
            // Initialize acc with the current object if acc is not defined
            if (!acc) {
              return { ...m, discount: discount.amount }
            }

            // Update acc if discount is greater than acc's discount
            if (discount.amount > acc.discount) {
              return { ...m, discount: discount.amount }
            }
          }

          return acc
        }, null)
      return siteConfig.memberships.find((mem) => mem.categoryId === highestDiscount?.categoryId)?.discounts?.[type]
        ?.amount
    },
    async flushCaches() {
      // XXX TODO Use existing method on userStore?
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/open?skipCache=1`)
        .then((response) => {})
        .catch((e) => {
          console.log('error flushing caches', e)
        })
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/programs?skipCache=1`)
        .then((response) => {})
        .catch((e) => {
          console.log('error flushing caches', e)
        })
      axios
        .get(`${siteConfig.apiBaseUrl}/classes/instructors?skipCache=1`)
        .then((response) => {})
        .catch((e) => {
          console.log('error flushing caches', e)
        })
    },

    async fetchPreAuthorized() {
      try {
        const response = await axios.get(`/sites/${siteConfig.key}/hashed.json`)
        const hashed = response.data
        this.preAuthorizedEmails = hashed
      } catch (e) {
        console.log('error fetching waitlist', e)
      }
    },

    // true/false when the check succeeds, null when it couldn't be completed
    async checkExistenceOfAccount(username) {
      try {
        const response = await axios.get(
          `${siteConfig.apiBaseUrl}/users/checkexistenceofaccount/${encodeURIComponent(normalizeEmail(username))}`
        )
        const exists = response.data?.results?.accountExistWarning
        if (!response.data?.ok || typeof exists !== 'boolean') return null

        return exists
      } catch (e) {
        console.log('error checking account existence', e)
        return null
      }
    },

    async fetchGroups() {
      if (this.loggedIn && this.account?.personId) {
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/groups-for-person?person_id=${this.account.personId}`
          )
          this.groupIds = response.data?.results?.map((group) => group.group_id)
        } catch (e) {
          console.log('error fetching groups', e)
        }
      }
    },

    async fetchRegistrationFees() {
      if (this.loggedIn && this.account?.personId) {
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/household-registration-fees?household_id=${this.account.householdId}`
          )
          this.registrationFees = response.data.results
        } catch (e) {
          console.log('error fetching groups', e)
        }
      }
    },

    async fetchMembershipsForHousehold() {
      if (this.loggedIn && this.account.householdId) {
        try {
          const response = await axios.get(
            `${siteConfig.apiBaseUrl}/reports/queries/household-memberships?household_id=${this.account.householdId}`
          )
          this.membershipsInHousehold = response.data.results
          this.fetchedMemberships = true
        } catch (e) {
          console.log('error fetching memberships in household', e)
        }
      }
    },

    async fetchBalance() {
      if (!this.account || !this.loggedIn) return
      if (this.fetchingBalance) return

      this.fetchingBalance = true

      axios
        .get(`${siteConfig.apiBaseUrl}/users/${this.account.personId}/balance`)
        .then((response) => {
          this.accountItems = response.data.items

          // XXX FIXME HACK Only show balance if there is just one item
          // if (this.accountItems.length <= 1) {
          this.balance = response.data.balance
          this.fetchedBalance = true
          this.fetchingBalance = false
          // }
        })
        .catch((error) => {
          console.log('fetchBalance error', error)
          this.fetchedBalance = true
          this.fetchingBalance = false
        })
    },

    async fetchHouseholdAccountBalance(id) {
      if (!this.account || !this.loggedIn) return
      if (this.fetchingBalance) return

      this.fetchingBalance = true

      try {
        const response = await axios.get(`${siteConfig.apiBaseUrl}/users/${id}/balance`)

        this.fetchedBalance = true
        this.fetchingBalance = false

        return response.data
      } catch (error) {
        console.log('fetchHouseholdAccountBalance error', error)
        this.fetchedBalance = true
        this.fetchingBalance = false
        throw error
      }
    },

    async fetchCampaigns() {
      axios
        .get(`${siteConfig.apiBaseUrl}/development/campaigns`)
        .then((response) => {
          const data = response.data

          if (data.ok) {
            this.campaigns = data.campaigns
          } else {
            console.warn('error loading campaigns')
          }
        })
        .catch((error) => {
          console.log('fetchCampaigns error', error)
        })
    },

    updateSavedPayments(card) {
      let formattedDate = ''
      if (card.expireDate) {
        const date = new Date(card.expireDate)

        // Get the two-digit month and year
        const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are zero-indexed, so add 1
        const year = date.getFullYear().toString().slice(-2)

        formattedDate = `${month}/${year}`
      }

      const newPayment = {
        type: card.profileType,
        profileId: card.profileId,
        cardRef: card.cardRef,
        holder: card.profileName,
        id: card.providerToken,
        expireDate: `${formattedDate}`,
        paymentProfileId: card.paymentProfileId ?? card.providerToken,
      }

      if (this.savedPayments.length === 0) {
        this.savedPayments.push(newPayment)
      } else {
        this.savedPayments[0] = newPayment
      }
    },

    deleteSavedPayments() {
      // mutate in place to keep refs in sync
      this.savedPayments.splice(0)
    },

    async fetchSavedPayments() {
      if (!this.account || !this.loggedIn) return
      await axios
        .get(`${siteConfig.apiBaseUrl}/paymentprocessing/findpaymentprofile/${this.account.personId}`)
        .then((response) => {
          let card = response.data['data']
          if (!card || this.savedPayments.filter((p) => p.id === card.providerToken).length > 0) {
            return
          }
          this.updateSavedPayments(card)
        })
        .catch((error) => {
          console.log('fetchSavedPayments error', error)
        })
    },

    async checkSession() {
      const session = getStoredSession()

      if (session) {
        if (isSessionExpired(session)) {
          this.logOut()
          return
        }
        this.firstName = session.name
        this.token = session.token

        await axios
          .post(`${siteConfig.apiBaseUrl}/users/lookup`, { token: this.token })
          .then((response) => {
            this.account = response.data.account
            this.credit =
              this.account?.acctBal < 0 && !siteConfig.features.disableBalanceCheck
                ? Math.abs(this.account?.acctBal)
                : 0
            this.loggedIn = true
            this.fetchGroups()
          })
          .catch((error) => {
            console.log('checkSession user lookup error', error)

            if ([400, 401].includes(error.response?.status)) {
              clearStoredSession()
            }
            this.account = null
            this.loggedIn = false
            this.firstName = null
            this.token = null
          })
          .finally((data) => {
            this.loading = false
            this.fetchMembershipsForHousehold()
            this.fetchSavedPayments()
            if (siteConfig.features.registrationFee) {
              this.fetchRegistrationFees()
            }
          })
      } else {
        this.loading = false
      }
    },

    logOut() {
      this.loggedIn = false
      this.token = null
      this.firstName = null
      this.account = null
      this.loggedOut = true
      this.accountItems = []
      this.balance = 0
      localStorage.removeItem(`${siteConfig.key}Auth`)
      sessionStorage.removeItem(`shouldNotAddDonationAutomatically`)
      const cartStore = useCartStore()
      cartStore.emptyCart()
      goHome(router).then(() => {
        location.reload()
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
